import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  Button,
  Paper,
  Typography,
  TextField as MuiTextField,
} from "@mui/material";
import styled from "@emotion/styled";
import React from "react";
import { Helmet } from "react-helmet-async";
import { Formik } from "formik";
import * as Yup from "yup";
import { spacing } from "@mui/system";
import LogoImage from "../../vendor/tiehoitokunta_logo_transparent.png";

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const UserRoot = styled.div`
  max-width: 540px;
  margin: 0 auto;
  justify-content: flex-start;
  align-items: center;
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  background-color: #f7f9fc;
`;

const Wrapper = styled(Paper)`
  width: 100%;
  padding: ${(props) => props.theme.spacing(6)};
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(6)};
  }
`;

const Logo = styled("img")`
  width: 150px;
  height: 150px;
  margin-bottom: 2rem;
`;

const PasswordChange = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(5);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token) {
      setError("Linkki puuttuu tai on virheellinen.");
      return;
    }

    axios
      .get(`${baseUrl}/auth/password-changed?token=${token}`)
      .then(() => setSuccess(true))
      .catch(() => {
        setError("Linkki ei ole voimassa tai on jo käytetty.");
      });
  }, [location.search]);

  // Kun salasana on vaihdettu onnistuneesti
  useEffect(() => {
    if (passwordChanged && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (passwordChanged && countdown === 0) {
      navigate("/");
    }
  }, [passwordChanged, countdown, navigate]);

  return (
    <React.Fragment>
      <Helmet title="Salasanan vaihto" />
      <UserRoot>
        <Logo src={LogoImage} alt="Tiehoitokunta logo" />
        <Wrapper>
          <Typography
            component="h1"
            variant="h5"
            align="center"
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            Salasanan vaihto
          </Typography>

          {/* ✅ Näytetään onnistumisviesti ja countdown */}
          {passwordChanged && (
            <Typography align="center" color="success.main" sx={{ mb: 2 }}>
              Salasana onnistuneesti vaihdettu! <br />
              Palataan etusivulle {countdown} sekunnin kuluttua...
            </Typography>
          )}

          {success && !passwordChanged && (
            <Formik
              initialValues={{ newPassword: "", confirmPassword: "" }}
              validationSchema={Yup.object({
                newPassword: Yup.string()
                  .min(8, "Salasanan tulee olla vähintään 8 merkkiä")
                  .required("Uusi salasana vaaditaan"),
                confirmPassword: Yup.string()
                  .oneOf([Yup.ref("newPassword")], "Salasanat eivät täsmää")
                  .required("Vahvista salasana"),
              })}
              onSubmit={async (values, { setSubmitting, setErrors }) => {
                try {
                  const params = new URLSearchParams(location.search);
                  const token = params.get("token");

                  await axios.post(`${baseUrl}/auth/set-new-password`, {
                    token,
                    newPassword: values.newPassword,
                  });

                  setPasswordChanged(true);
                  setCountdown(5);
                } catch (error: any) {
                  setErrors({
                    confirmPassword: "Salasanan vaihto epäonnistui",
                  });
                  setSubmitting(false);
                }
              }}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
                isSubmitting,
              }) => (
                <form
                  noValidate
                  onSubmit={handleSubmit}
                  style={{ width: "100%" }}
                >
                  <TextField
                    type="password"
                    name="newPassword"
                    label="Uusi salasana"
                    value={values.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.newPassword && errors.newPassword)}
                    helperText={touched.newPassword && errors.newPassword}
                    fullWidth
                    my={2}
                  />
                  <TextField
                    type="password"
                    name="confirmPassword"
                    label="Vahvista salasana"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(
                      touched.confirmPassword && errors.confirmPassword
                    )}
                    helperText={
                      touched.confirmPassword && errors.confirmPassword
                    }
                    fullWidth
                    my={2}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ mt: 2 }}
                  >
                    Vaihda salasana
                  </Button>
                </form>
              )}
            </Formik>
          )}

          {error && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
                textAlign: "center",
              }}
            >
              <Typography color="error" variant="body1">
                {error}
              </Typography>
              <Button href={"/"} variant="contained" color="primary">
                Etusivu
              </Button>
            </div>
          )}
        </Wrapper>
      </UserRoot>
    </React.Fragment>
  );
};

export default PasswordChange;
